import type { Structure } from '@wix/thunderbolt-becky-types'
import type { CompsInFoldMap } from '../types'
import { getComponentDescendantsBfs, BfsPredicate, getChildrenCb } from './getComponentDescendants'

// This number is determined by taking the 80th percentile out of all heights in this link: https://experienceleague.adobe.com/docs/target/using/experiences/vec/mobile-viewports.html?lang=en
export const HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE = 731
//  This number is determined by taking the most common desktop height as specified in this link: https://www.browserstack.com/guide/ideal-screen-sizes-for-responsive-design
export const HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP = 1080

export const compsInFoldTypesList = ['WPhoto', 'ImageX']

type PagesObject = { nonPopupPages: Array<string>; popupPages: Array<string> }

const getAllPages = (structure: Structure): PagesObject => {
	const allPages = Object.keys(structure).filter((compId) => structure[compId].type === 'Page')
	return allPages.reduce<PagesObject>(
		(acc, pageId) => {
			const children = structure[pageId].components
			const isPopupPage =
				children && children.length > 0 && structure[children[0]]?.componentType.endsWith('PopupContainer')
			acc[isPopupPage ? 'popupPages' : 'nonPopupPages'].push(pageId)
			return acc
		},
		{ nonPopupPages: [], popupPages: [] }
	)
}

export const getCompsInFirstFold = (structure: Structure, threshold: number): CompsInFoldMap => {
	const { nonPopupPages, popupPages } = getAllPages(structure)
	const pagesContainerY = structure.PAGES_CONTAINER?.layout.y || 0

	const pagesAbsoluteY = nonPopupPages.reduce(
		(acc, pageId) => Object.assign(acc, { [pageId]: (structure[pageId].layout.y || 0) + pagesContainerY }),
		{}
	)
	const popupPagesAbsoluteY = popupPages.reduce(
		(acc, popupPageId) => Object.assign(acc, { [popupPageId]: structure[popupPageId].layout.y || 0 }),
		{}
	)

	const compsAbsoluteY: Record<string, number> = {
		...pagesAbsoluteY,
		...popupPagesAbsoluteY,
		masterPage: structure.masterPage?.layout.y || 0,
	}
	const fixedPositionComps: Record<string, boolean> = {}
	const isInFirstFoldPredicate: BfsPredicate = (nodeId) => {
		if ([...nonPopupPages, ...popupPages, 'masterPage'].includes(nodeId)) {
			return compsAbsoluteY[nodeId] < threshold
		}
		const parentId: string | null | undefined = structure[nodeId!]?.parent
		const isParentFixedPosition =
			parentId && (structure[parentId].layout.fixedPosition || fixedPositionComps[parentId])
		const isNodeFixedPosition = structure[nodeId]?.layout.fixedPosition
		if (isNodeFixedPosition || isParentFixedPosition) {
			fixedPositionComps[nodeId] = true
		}
		compsAbsoluteY[nodeId] =
			(parentId && !isParentFixedPosition ? compsAbsoluteY[parentId] : 0) + (structure[nodeId]?.layout.y || 0)
		return compsAbsoluteY[nodeId] < threshold
	}

	return getComponentDescendantsBfs({
		roots: [...nonPopupPages, ...popupPages, 'masterPage'],
		predicate: isInFirstFoldPredicate,
		getChildren: getChildrenCb(structure),
		shouldStopUponFalsePredicate: true,
	})
}
